import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, ShoppingCart, Bot, MessageCircle, Palette, Zap, Blocks, Sparkles } from 'lucide-react';
import ErrorBoundary from '../components/ErrorBoundary';

const Hero3D = lazy(() => import('../components/Hero3D'));

const services = [
  { icon: <Palette size={28} />, title: 'Landing Pages', description: 'Páginas de alta conversão otimizadas para resultados' },
  { icon: <ShoppingCart size={28} />, title: 'E-commerce', description: 'Lojas virtuais completas e personalizadas' },
  { icon: <Code size={28} />, title: 'Sistemas Web', description: 'Plataformas e dashboards sob medida' },
  { icon: <Bot size={28} />, title: 'Inteligência Artificial', description: 'Soluções inteligentes com IA de ponta' },
  { icon: <MessageCircle size={28} />, title: 'Automação WhatsApp', description: 'Automatize seu atendimento no WhatsApp Business' },
  { icon: <Sparkles size={28} />, title: 'Chatbots', description: 'Atendimento automatizado 24/7' },
  { icon: <Zap size={28} />, title: 'Integrações', description: 'Conecte seus sistemas e automatize processos' },
  { icon: <Blocks size={28} />, title: 'Web Design', description: 'Design premium e experiência excepcional' }
];

const testimonials = [
  { name: 'Carlos Silva', company: 'CEO, TechStart', text: 'A Done Technology transformou nossa visão em realidade. O nível de profissionalismo e qualidade é incomparável.' },
  { name: 'Ana Costa', company: 'Fundadora, Fashion Brand', text: 'Nosso e-commerce ficou incrível! As vendas triplicaram nos primeiros 3 meses.' },
  { name: 'Roberto Mendes', company: 'Diretor, FinTech', text: 'Entrega rápida, comunicação clara e resultado acima das expectativas.' }
];

const process = [
  { step: '01', title: 'Discovery', description: 'Entendemos seu negócio e objetivos' },
  { step: '02', title: 'Planejamento', description: 'Criamos a estratégia e arquitetura' },
  { step: '03', title: 'Design', description: 'Desenvolvemos a experiência visual' },
  { step: '04', title: 'Desenvolvimento', description: 'Construindo com as melhores tecnologias' },
  { step: '05', title: 'Lançamento', description: 'Deploy e acompanhamento de resultados' }
];

export default function Home() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* 3D Background */}
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <Hero3D />
          </Suspense>
        </ErrorBoundary>
        
        {/* Tech grid overlay */}
        <div className="absolute inset-0 -z-10 opacity-30" style={{
          backgroundImage: 'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)'
        }} />
        
      {/* Vignette gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#080710]/40 via-[#080710]/60 to-[#080710]" style={{ zIndex: 1 }} />
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#22D3EE]/5 border border-[#22D3EE]/20 rounded-full px-4 py-1.5 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-[#22D3EE] animate-pulse" />
              <span className="text-xs text-[#22D3EE] tracking-wider">AGÊNCIA DIGITAL PREMIUM</span>
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-6">
              Construímos <span className="text-[#22D3EE]" style={{ textShadow: '0 0 30px rgba(34,211,238,0.5)' }}>plataformas</span>
              <br />
              que <span className="bg-gradient-to-r from-[#22D3EE] to-[#4F46E5] bg-clip-text text-transparent">escalam</span> negócios
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-10 max-w-2xl mx-auto leading-relaxed">
              Desenvolvimento web, IA, automação e soluções tecnológicas com padrão de startup milionária — para empresas que não aceitam o comum.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/orcamento"
                data-testid="hero-cta-primary"
                className="group relative bg-[#22D3EE] text-[#080710] px-7 py-3.5 rounded-md font-semibold text-base tracking-wide hover:shadow-[0_0_40px_rgba(34,211,238,0.5)] transition-all"
              >
                Iniciar Projeto →
              </Link>
              <a
                href="#servicos"
                data-testid="hero-cta-secondary"
                className="bg-transparent border border-white/15 text-[#F8FAFC] px-7 py-3.5 rounded-md font-semibold text-base tracking-wide hover:border-[#22D3EE] hover:text-[#22D3EE] transition-all"
              >
                Explorar serviços
              </a>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#22D3EE]/30 rounded-full flex items-start justify-center p-1.5">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-[#22D3EE] rounded-full"
            />
          </div>
        </motion.div>
      </section>
      
      {/* Services Section */}
      <section id="servicos" className="py-24 px-6 relative" data-testid="services-section">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-xs text-[#22D3EE] tracking-[0.3em] mb-3">SERVIÇOS</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 max-w-2xl">
              Soluções <span className="text-[#22D3EE]">completas</span> para impulsionar seu negócio
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {services.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -4 }}
                data-testid={`service-card-${idx}`}
                className="group bg-[#0d0f17] border border-white/5 rounded-xl p-6 hover:border-[#22D3EE]/40 hover:shadow-[0_0_40px_rgba(34,211,238,0.08)] transition-all cursor-pointer"
              >
                <div className="text-[#22D3EE] mb-4 group-hover:scale-110 transition-transform">{service.icon}</div>
                <h3 className="text-lg font-semibold mb-2 text-[#F8FAFC]">{service.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-24 px-6 bg-[#0d0f17]/40 relative" data-testid="testimonials-section">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-xs text-[#22D3EE] tracking-[0.3em] mb-3">DEPOIMENTOS</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              O que nossos <span className="text-[#22D3EE]">clientes</span> dizem
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`testimonial-card-${idx}`}
                className="bg-[#080710] border border-white/5 rounded-xl p-6"
              >
                <div className="text-[#22D3EE] mb-4 text-4xl leading-none">"</div>
                <p className="text-[#F8FAFC] text-sm leading-relaxed mb-6">{testimonial.text}</p>
                <div>
                  <p className="font-semibold text-sm text-[#F8FAFC]">{testimonial.name}</p>
                  <p className="text-xs text-[#94A3B8]">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="py-24 px-6" data-testid="process-section">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-xs text-[#22D3EE] tracking-[0.3em] mb-3">PROCESSO</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Do planejamento ao <span className="text-[#22D3EE]">lançamento</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {process.map((step, idx) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`process-step-${idx}`}
                className="relative"
              >
                <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-5">
                  <div className="text-3xl font-bold text-[#22D3EE]/30 mb-3">{step.step}</div>
                  <h3 className="text-base font-semibold mb-2 text-[#F8FAFC]">{step.title}</h3>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section id="contato" className="py-32 px-6 relative" data-testid="cta-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#22D3EE]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter leading-tight mb-6">
              Pronto para construir algo
              <br />
              <span className="text-[#22D3EE]">extraordinário</span>?
            </h2>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-10 max-w-xl mx-auto">
              Receba uma proposta personalizada em até 24 horas. Sem compromisso.
            </p>
            <div className="flex justify-center">
              <Link
                to="/orcamento"
                data-testid="final-cta-orcamento"
                className="bg-[#22D3EE] text-[#080710] px-10 py-4 rounded-md font-semibold text-base hover:shadow-[0_0_40px_rgba(34,211,238,0.5)] transition-all inline-block"
              >
                Solicitar Orçamento →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5" data-testid="footer">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#94A3B8]">
            © 2026 Done Technology. Todos os direitos reservados.
          </p>
          <p className="text-xs text-[#94A3B8]">
            Construído com tecnologia premium ⚡
          </p>
        </div>
      </footer>
    </div>
  );
}
